import { getCookieDomain, getCookieDomainFromHost } from '@ifixit/helpers';
import Cookies from 'js-cookie';

// A cookie on the parent domain, not localStorage: translated guides on
// de.ifixit.com and the other language subdomains link to product pages on
// www.ifixit.com, which can't read another origin's storage.
const COOKIE_NAME = 'productListClicks';
// Long enough to read the product page and come back to it later that day.
const LIST_CLICK_CREDIT_MS = 24 * 60 * 60 * 1000;
// The cookie goes with every request to every ifixit.com host, images
// included, so it keeps only the last few clicks, each written as
// `handle:listId:seconds` (seconds in base 36) and joined by `|`. Cookies
// carry both separators as is, where JSON's quotes and commas get
// percent-encoded to three bytes each.
const MAX_REMEMBERED_CLICKS = 10;
const CLICK_SEPARATOR = '|';
const FIELD_SEPARATOR = ':';
const SEPARATORS = /[|:]/;
// Product pages live at /products/<handle>, after the store's locale prefix
// (/en-gb) when there is one.
const PRODUCT_PATH = /^(?:\/[a-z]{2}-[a-z]{2})?\/products\/([^/]+)\/?$/i;
const MIDDLE_BUTTON = 1;

/**
 * `data-list-id`s of the lists that have no GA4 `item_list_id` of their own.
 * Orders keep them in `_clicked_from`, so renaming one splits its sales in
 * reports.
 */
export const LIST_CLICK_IDS = {
   cartDrawerCrossSell: 'cart_drawer_cross_sell',
   cartPageCrossSell: 'cart_page_cross_sell',
   featuredProducts: 'featured_products',
   instantSearch: 'instant_search',
   productAdCardsAnswers: 'product_ad_cards_answers',
   productAdCardsGuide: 'product_ad_cards_guide',
   productComparison: 'product_comparison',
   search: 'search',
} as const;

type ListClick = { productHandle: string; listId: string; clickedAt: number };

/**
 * Remembers which product list the visitor opened a product page from, so
 * adding that product on its page can credit the list. A list takes part by
 * naming itself in a `data-list-id` attribute on an element around its
 * product links: its GA4 `item_list_id` when it has one.
 */
export function listenForListClicks({ hasConsent }: { hasConsent: () => boolean }) {
   const onClick = (event: MouseEvent) => {
      // A right click fires auxclick too, and opens nothing on its own.
      if (event.type === 'auxclick' && event.button !== MIDDLE_BUTTON) return;
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!(link instanceof HTMLAnchorElement)) return;
      const listId = link.closest<HTMLElement>('[data-list-id]')?.dataset?.listId;
      if (listId && hasConsent()) {
         rememberListClickOnProductLink({ href: link.href, listId });
      }
   };
   // Capture, so the click is seen before a list's own handler closes the
   // cart drawer and takes the link out of the page.
   document.addEventListener('click', onClick, { capture: true });
   document.addEventListener('auxclick', onClick, { capture: true });
   return () => {
      document.removeEventListener('click', onClick, { capture: true });
      document.removeEventListener('auxclick', onClick, { capture: true });
   };
}

/** The list the product was last clicked in, if that was within the last day. */
export function getRecentListClick(productHandle: string): string | undefined {
   const now = Date.now();
   return readListClicks().find(
      click => click.productHandle === productHandle && isRecent(click, now)
   )?.listId;
}

function rememberListClickOnProductLink({ href, listId }: { href: string; listId: string }) {
   const productHandle = productHandleFromHref(href);
   if (!productHandle || SEPARATORS.test(productHandle) || SEPARATORS.test(listId)) {
      return;
   }
   const now = Date.now();
   const otherClicks = readListClicks()
      .filter(click => click.productHandle !== productHandle && isRecent(click, now))
      .sort((a, b) => b.clickedAt - a.clickedAt)
      .slice(0, MAX_REMEMBERED_CLICKS - 1);
   writeListClicks([{ productHandle, listId, clickedAt: now }, ...otherClicks]);
}

function productHandleFromHref(href: string): string | undefined {
   try {
      const url = new URL(href, location.href);
      const match = canReadListClicks(url) ? url.pathname.match(PRODUCT_PATH) : null;
      return match ? decodeURIComponent(match[1]) : undefined;
   } catch {
      return undefined;
   }
}

// Another store's /products/ page is not one of ours, and only our hosts
// receive the cookie.
function canReadListClicks(url: URL) {
   const cookieDomain = getCookieDomain(location);
   return cookieDomain
      ? getCookieDomainFromHost(url.hostname) === cookieDomain
      : url.hostname === location.hostname;
}

function isRecent(click: ListClick, now: number) {
   return now - click.clickedAt < LIST_CLICK_CREDIT_MS;
}

function readListClicks(): ListClick[] {
   try {
      return (Cookies.get(COOKIE_NAME) ?? '')
         .split(CLICK_SEPARATOR)
         .map(parseListClick)
         .filter(click => click != null);
   } catch {
      return [];
   }
}

function parseListClick(stored: string): ListClick | null {
   const fields = stored.split(FIELD_SEPARATOR);
   const [productHandle, listId, seconds] = fields;
   const clickedAt = Number.parseInt(seconds ?? '', 36) * 1000;
   return fields.length === 3 && productHandle && listId && Number.isFinite(clickedAt)
      ? { productHandle, listId, clickedAt }
      : null;
}

function writeListClicks(clicks: ListClick[]) {
   const value = clicks
      .map(({ productHandle, listId, clickedAt }) =>
         [productHandle, listId, Math.floor(clickedAt / 1000).toString(36)].join(FIELD_SEPARATOR)
      )
      .join(CLICK_SEPARATOR);
   try {
      Cookies.set(COOKIE_NAME, value, {
         domain: getCookieDomain(location),
         expires: new Date(Date.now() + LIST_CLICK_CREDIT_MS),
         sameSite: 'lax',
         secure: location.protocol === 'https:',
      });
   } catch {
      // Blocked cookies only lose the credit for this click.
   }
}
